import { Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import { memo } from "react";
import { PresetPill } from "@funnel-ui/primitives/PresetPill";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";
import { ResponsiveAsset } from "@funnel-ui/primitives/ResponsiveAsset";

export type AttributeItem = {
  id: string;
  label: string;
  value: string;
  imageUrl?: string;
  editable?: boolean;
};

type AttributeReviewProps = {
  headline: string;
  description?: string;
  attributes: AttributeItem[];
  mode?: "pills" | "image_summary";
  onEdit?: (id: string) => void;
  className?: string;
  testId?: string;
};

const AttributeReviewImpl = ({
  headline,
  description,
  attributes,
  mode = "pills",
  onEdit,
  className,
  testId,
}: AttributeReviewProps) => (
  <section data-testid={testId} className={`flex flex-col gap-5 ${className ?? ""}`}>
    <StepHeading title={headline} description={description} />

    {mode === "pills" ? (
      <div className="flex flex-col gap-2">
        {attributes.map((attr) => (
          <Button
            key={attr.id}
            variant="outline"
            isDisabled={attr.editable === false}
            onPress={() => onEdit?.(attr.id)}
            className="group h-auto w-full justify-between p-3 text-left"
            style={{ borderColor: "hsl(var(--brand-bg-foreground) / 0.10)" }}
          >
            <div className="flex flex-col gap-0.5">
              <span
                className="text-xs uppercase tracking-wide"
                style={{ color: "hsl(var(--hero-fg-muted))" }}
              >
                {attr.label}
              </span>
              <span
                className="text-sm font-semibold"
                style={{ color: "hsl(var(--brand-bg-foreground))" }}
              >
                {attr.value}
              </span>
            </div>
            {attr.editable !== false && (
              <Icon
                icon="gravity-ui:pencil"
                width={16}
                height={16}
                className="opacity-50 transition-opacity group-hover:opacity-100"
              />
            )}
          </Button>
        ))}
      </div>
    ) : (
      <div className="flex flex-col items-center gap-4">
        {attributes.find((a) => a.imageUrl) && (
          <ResponsiveAsset
            src={attributes.find((a) => a.imageUrl)!.imageUrl}
            alt=""
            loading="eager"
            widthHint={160}
            className="h-40 w-40 rounded-full object-cover"
          />
        )}
        <div className="flex flex-wrap justify-center gap-2">
          {attributes.map((attr) => (
            <PresetPill
              key={attr.id}
              label={`${attr.label}: ${attr.value}`}
              onPress={() => onEdit?.(attr.id)}
              disabled={attr.editable === false}
            />
          ))}
        </div>
      </div>
    )}
  </section>
);

export const AttributeReview = memo(AttributeReviewImpl);

export default AttributeReview;
